/* eslint-disable max-len */

'use client';

import { motion, useSpring, useTransform, useInView, AnimatePresence, cubicBezier, useAnimate, stagger } from 'framer-motion';
import { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { useInViewScroll, useSanitizedTranslation, XumoButton, StackedContent } from 'ui';
import { useMeasure, useWindowSize } from '@uidotdev/usehooks';
import Image from 'next/image';
import Link from 'next/link';

type Product = {
  id: string;
  image: { src: string; alt: string };
  mobileImage?: { src: string; alt: string };
  logo?: { src: string; alt: string };
  heading: string;
  body?: string;
  button?: { label: string; href: string };
  availability?: { appStore?: boolean; items?: { store: string; link: string } };
};

const ProductsGalleryItem = ({ product }: { product: Product }) => {
  const { t } = useSanitizedTranslation();

  const { image, logo, heading, body, button, availability } = product;

  const logoMap = {
    amazon: '/static/images/refresh/2025/logos/amazon.svg',
    bestBuy: '/static/images/refresh/2025/logos/bestbuy.svg',
    walmart: '/static/images/refresh/2025/logos/walmart.svg',
    xfinity: '/static/images/refresh/2025/logos/xfinity.svg',
    spectrum: '/static/images/refresh/2025/logos/spectrum.svg',
  };

  return (
    <div className="relative flex aspect-[1471/650] origin-top overflow-clip rounded-2xl lg:w-[1050px] xl:aspect-[1471/650] xl:w-[1250px] 2xl:aspect-[1471/620] 2xl:w-[1350px]">
      <Image src={image.src} alt={image.alt} priority fill sizes="100vw" className="top-0 h-[100%] w-full object-cover" />

      {/* Gradient overlay */}
      <div className="absolute inset-0 block h-full w-full bg-gradient-to-r from-[#5B2E4F] from-[33%] to-transparent xl:hidden" />

      {/* Content */}
      <div className="z-50 flex h-full w-full flex-col justify-between px-12 py-10 text-white lg:w-2/3 xl:w-1/2 xl:px-14 xl:py-12">
        <Image src={logo?.src} alt={logo?.alt} width={210} height={40} />

        <h3 className="type-card-heading" data-i18n dangerouslySetInnerHTML={{ __html: t(heading) }} />
        <p className="type-card-small w-[90%]" data-i18n dangerouslySetInnerHTML={{ __html: t(body) }} />

        <div className="">
          <XumoButton primary light {...button} />
        </div>

        <div className="flex">
          {availability?.appStore ? (
            <div className="flex items-center gap-4">
              <a href="https://play.google.com/store/apps/details?id=com.xumo.xumo" target="_blank" rel="noopener noreferrer">
                <Image src="/static/images/refresh/google-play.png" alt="Get it on Google Play" width={159} height={59} />
              </a>
              <a href="https://apps.apple.com/us/app/xumo-play-stream-tv-movies/id1068337978" target="_blank" rel="noopener noreferrer">
                <Image src="/static/images/refresh/apple-app-store.png" alt="Download on the App Store" width={139} height={59} />
              </a>
            </div>
          ) : (
            <div className="flex items-center gap-6">
              <p data-i18n>Available from:</p>

              {availability?.items?.map((item, idx) => {
                const { store, link } = item;
                const bestBuy = store === 'bestBuy';

                return (
                  <a href={link} target="_blank" rel="noopener noreferrer" className="">
                    <Image key={'galleryCardLogo' + idx} src={logoMap[store]} width={0} height={0} className={`${bestBuy ? 'h-[40px]' : 'h-[30px]'} w-auto`} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MobileProductsGalleryItem = ({ product }: { product: Product }) => {
  const { t } = useSanitizedTranslation();
  const { mobileImage, image, logo, heading, body, button, availability } = product;

  // Use mobileImage if available, fallback to regular image
  const displayImage = mobileImage || image;

  const logoMap = {
    amazon: '/static/images/refresh/2025/logos/amazon.svg',
    bestBuy: '/static/images/refresh/2025/logos/bestbuy.svg',
    walmart: '/static/images/refresh/2025/logos/walmart.svg',
    xfinity: '/static/images/refresh/2025/logos/xfinity.svg',
    spectrum: '/static/images/refresh/2025/logos/spectrum.svg',
  };

  return (
    <div className="flex w-[85vw] max-w-[550px] flex-col overflow-hidden rounded-2xl">
      {/* Image wrapper: full width, fixed height */}
      <div className="relative aspect-[380/250] w-full">
        <Image src={displayImage.src} alt={displayImage.alt} fill className="object-cover" />
      </div>

      <div className="flex h-full min-h-[430px] w-full flex-col items-center justify-between gap-4 rounded-b-2xl bg-gradient-to-l from-[#5B2E4F] to-[#150E14] px-4 py-8 text-center text-white">
        {logo?.src && <Image src={logo.src} alt={logo.alt ?? ''} width={160} height={30} />}

        <h3 className="type-card-heading" data-i18n dangerouslySetInnerHTML={{ __html: t(heading) }} />
        <p className="type-body-base" data-i18n dangerouslySetInnerHTML={{ __html: t(body) }} />

        <div>
          <XumoButton primary light {...button} />
        </div>

        {availability?.appStore && (
          <div className="flex items-center gap-2">
            <a href="https://play.google.com/store/apps/details?id=com.xumo.xumo" target="_blank" rel="noopener noreferrer">
              <Image src="/static/images/refresh/google-play.png" alt="Get it on Google Play" width={159} height={59} />
            </a>
            <a href="https://apps.apple.com/us/app/xumo-play-stream-tv-movies/id1068337978" target="_blank" rel="noopener noreferrer">
              <Image src="/static/images/refresh/apple-app-store.png" alt="Download on the App Store" width={139} height={59} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const ProductsGallery = ({ stackedContent, products }) => {
  const { ref } = useInViewScroll([0, 1], ['start 100vh', 'end 0vh']);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const { width: windowWidth } = useWindowSize();
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 1100);
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, []);

  const buttonMap = {
    tv: 'Xumo TV',
    streamBox: 'Xumo Stream Box',
    play: 'Xumo Play',
  };

  // Desktop offsets for centering via transform
  const [offsets, setOffsets] = useState<number[]>([]);
  useEffect(() => {
    if (isMobile || !containerRef.current || !cardRefs.current.length) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    const newOffsets = cardRefs.current.map(card => {
      if (!card) return 0;
      const r = card.getBoundingClientRect();
      const cardCenter = r.left + r.width / 2;
      return containerCenter - cardCenter;
    });
    setOffsets(newOffsets);
  }, [products, windowWidth, isMobile]);

  // Scroll to index function for mobile
  const scrollToIndex = useCallback(
    (index: number) => {
      const el = containerRef.current;
      const card = cardRefs.current[index];
      if (!el || !card || !isMobile) return;

      setIsUserScrolling(true);

      // Calculate the target scroll position to center the card
      const cardLeft = card.offsetLeft;
      const cardWidth = card.offsetWidth;
      const containerWidth = el.clientWidth;
      const targetScroll = cardLeft - (containerWidth - cardWidth) / 2;

      el.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'smooth',
      });

      // Reset user scrolling flag after animation
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 600);
    },
    [isMobile],
  );

  const handleSelect = (index: number) => {
    setCurrentIndex(index);
    if (isMobile) {
      scrollToIndex(index);
    }
  };

  // Improved scroll synchronization for mobile
  useEffect(() => {
    if (!isMobile || !containerRef.current) return;

    const el = containerRef.current;
    let rafId = 0;

    const updateCurrentIndex = () => {
      if (isUserScrolling) return; // Don't update during programmatic scrolling

      const scrollLeft = el.scrollLeft;
      const containerWidth = el.clientWidth;
      const scrollCenter = scrollLeft + containerWidth / 2;

      let closestIndex = 0;
      let minDistance = Infinity;

      cardRefs.current.forEach((card, index) => {
        if (!card) return;

        const cardLeft = card.offsetLeft;
        const cardWidth = card.offsetWidth;
        const cardCenter = cardLeft + cardWidth / 2;
        const distance = Math.abs(scrollCenter - cardCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== currentIndex) {
        setCurrentIndex(closestIndex);
      }
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateCurrentIndex);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });

    // Initial update
    updateCurrentIndex();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [isMobile, currentIndex, isUserScrolling]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <motion.section ref={ref} className="relative z-40 flex w-full flex-col justify-center bg-white py-0" data-testid={'text-section'}>
      <div className="wrapper text-xumoDarkPlum">
        <StackedContent fullalways fullheight mobileCenterLg center space content={stackedContent} className={'text-xumoDarkPlum'} />
      </div>

      {/* Buttons */}
      <div className="mx-auto mb-6 mt-8 flex justify-center px-4 md:mt-16 xl:mb-8">
        <div className="flex flex-col items-center gap-3 md:flex-row md:gap-12">
          {products.map((product: Product, index: number) => (
            <div
              key={'productGalleryButton' + index}
              className={`rounded-full ${currentIndex === index ? 'to-xumoDarkPlum bg-gradient-to-b from-[#945B87] via-[#66345B] p-[2px]' : 'bg-white'}`}
            >
              <button
                onClick={() => handleSelect(index)}
                className={`weight-500 rounded-full text-lg tracking-[-1px] md:text-[24px] md:tracking-[-1.5px] ${
                  currentIndex === index ? 'text-xumoDarkPlum bg-white px-4 py-1 md:px-6' : 'text-xumoDarkPlum/50 bg-white px-4 py-1 md:px-6'
                }`}
              >
                {buttonMap[product.id]}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Carousel */}
      <div className="my-4 flex items-center justify-center md:my-8">
        <motion.div
          ref={containerRef}
          className={[
            'flex w-full items-center',
            isMobile ? 'scrollbar-hide touch-auto snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain px-4' : 'gap-8',
          ].join(' ')}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
          animate={isMobile ? {} : { x: offsets[currentIndex] || 0 }}
          transition={{
            type: 'spring',
            stiffness: 250,
            damping: 50,
            duration: 0.6,
          }}
        >
          {products.map((product: Product, index: number) => (
            <div
              key={product.id ?? index}
              ref={el => {
                cardRefs.current[index] = el;
              }}
              className={['transition-all duration-300', isMobile ? 'shrink-0 snap-center last:mr-4' : 'mr-8 last:mr-0'].join(' ')}
              style={
                isMobile
                  ? {
                      scrollSnapAlign: 'center',
                      scrollSnapStop: 'always',
                    }
                  : {}
              }
            >
              {isMobile ? <MobileProductsGalleryItem product={product} /> : <ProductsGalleryItem product={product} />}
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicators */}
      <div className="mb-4 mt-4 flex justify-center gap-2 lg:mt-0">
        {products.map((_, index) => (
          <button
            key={index}
            onClick={() => handleSelect(index)}
            className={`h-3 w-3 rounded-full transition-all duration-300 ${currentIndex === index ? 'bg-xumoDarkPlum w-8' : 'bg-xumoDarkPlum/30'}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </motion.section>
  );
};

export default ProductsGallery;
